import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from '../../../api/axiosClient';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { CircularProgress } from '@mui/material';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import { buildShopScopedPath } from '../../../utils/adminShopContext';
import { useTranslation } from 'react-i18next';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900/60 dark:backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const BookingRulesSettings = () => {
  const { shopId } = useAdminAuth();
  const { t } = useTranslation('admin');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    bookingAdvanceDays: 60,
    bookingMinNoticeMinutes: 120,
    bookingBufferMinutes: 5
  });

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosClient.get(buildShopScopedPath('/admin/settings/shop', shopId));
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error(t('admin.settings_error_loading'));
    } finally {
      setLoading(false);
    }
  }, [shopId, t]);

  const saveSettings = async () => {
    try {
      setSaving(true);
      await axiosClient.put(buildShopScopedPath('/admin/settings/shop', shopId), settings);
      toast.success(t('admin.settings_saved'));
    } catch (error) {
      console.error('Error saving settings:', error);
      const errorMessage = error.response?.data?.message || t('admin.settings_error_saving');
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex items-center gap-3 text-zinc-600 dark:text-zinc-400">
          <CircularProgress size={32} style={{ color: ACCENT }} />
          <span>{t('admin.settings_loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader
        title={t('admin.settings_booking_rules_title')}
        subtitle={t('admin.settings_booking_rules_subtitle')}
        icon={
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
      />

      <div className="p-4 md:p-5">
        <div className="space-y-6">
          {/* Advance Window */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              {t('admin.settings_advance_window')}
            </label>
            <input
              type="number"
              min="1"
              max="365"
              inputMode="numeric"
              value={settings.bookingAdvanceDays}
              onChange={(e) => handleInputChange('bookingAdvanceDays', parseInt(e.target.value || '0', 10))}
              className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
              style={{ '--accent': ACCENT }}
            />
            <p className="text-xs mt-1 text-zinc-500 dark:text-zinc-400">
              {t('admin.settings_advance_window_hint')}
            </p>
          </div>

          {/* Minimum Notice and Buffer Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Minimum Notice */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.settings_min_notice')}
              </label>
              <input
                type="number"
                min="0"
                max="1440"
                inputMode="numeric"
                value={settings.bookingMinNoticeMinutes}
                onChange={(e) => handleInputChange('bookingMinNoticeMinutes', parseInt(e.target.value || '0', 10))}
                className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              />
              <p className="text-xs mt-1 text-zinc-500 dark:text-zinc-400">
                {t('admin.settings_min_notice_hint')}
              </p>
            </div>

            {/* Buffer Time */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.settings_buffer_time')}
              </label>
              <input
                type="number"
                min="0"
                max="60"
                inputMode="numeric"
                value={settings.bookingBufferMinutes}
                onChange={(e) => handleInputChange('bookingBufferMinutes', parseInt(e.target.value || '0', 10))}
                className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              />
              <p className="text-xs mt-1 text-zinc-500 dark:text-zinc-400">
                {t('admin.settings_buffer_time_hint')}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            {t('admin.settings_booking_rules_note')}
          </p>
        </div>

        {/* Action Button */}
        <div className="px-4 md:px-5 pb-4 md:pb-5 pt-4 border-t border-white/10 dark:border-white/10">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={saveSettings}
            disabled={saving}
            className="w-full sm:w-auto px-6 py-2.5 text-sm rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: ACCENT }}
          >
            {saving ? (
              <>
                <CircularProgress size={16} style={{ color: 'white' }} />
                <span>{t('admin.settings_saving_changes')}</span>
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{t('admin.settings_save_changes')}</span>
              </>
            )}
          </motion.button>
        </div>
      </div>
    </Card>
  );
};

export default BookingRulesSettings;
